/**
 * Accounting > Attendance (Mercedes, Oct 29, 2026). Replaces her HR Dashboard
 * spreadsheet and the weekly attendance email.
 *
 * One row per person for the month: paid and unpaid time off, overtime split Crab
 * Place / Crab & Cruise, late and early, missed punches, points, flex days. Click a
 * person for the day-by-day detail, their standard times, and manual entries.
 *
 * Who may see it is decided on the server (attendance.js): the month summary is Greg and
 * Mercedes; the PIP section is only for the seven people Mercedes named, and Greg is not
 * one of them. The page only shows PIPs when the server says this person may see them.
 *
 * Round two (Mercedes, Nov 3, 2026): tabs for the team grid, schedules, missed punches,
 * points, holidays, alerts and the team roster (src/pages/attendance/). The managers can
 * open the page now and each sees only their own team (lib/attendance-team.js ACCESS).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { attendance } from '../services/api';
import TeamGrid from './attendance/TeamGrid';
import Schedule from './attendance/Schedule';
import { Points, Punches, Holidays, Alerts, TeamRoster } from './attendance/Panels';

const fmtDay = (d) => {
  if (!d) return '';
  const [y, m, dd] = String(d).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, dd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const fmtDow = (d) => { const [y, m, dd] = d.split('-').map(Number); return new Date(y, m - 1, dd).toLocaleDateString('en-US', { weekday: 'short' }); };
const monthLabel = (m) => { const [y, mm] = m.split('-').map(Number); return new Date(y, mm - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }); };
const hrs = (v) => (Number(v) ? Number(v).toFixed(2) : '');
const cnt = (v) => (Number(v) ? String(v) : '');
const clock = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};
const thisMonth = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date()).slice(0, 7);

const STATUS = {
  P: ['Present', '#e8f5ec', '#1c7c43'],
  V: ['PTO', '#e7f0fb', '#1d4f91'],
  S: ['Sick', '#fdf3e2', '#8a5a00'],
  H: ['Holiday', '#eef0f2', '#374151'],
  F: ['Flex day', '#f1ebfb', '#5b3a99'],
  O: ['Other paid', '#eef0f2', '#374151'],
  U: ['Unpaid', '#fdeaea', '#b42318'],
  C: ['Called out', '#fdeaea', '#b42318'],
  N: ['No time entered', '#fff6e0', '#8a5a00'],
  W: ['Weekend work', '#eef4fa', '#00395C'],
};

const th = { textAlign: 'right', padding: '6px 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px', color: 'var(--gray-500, #6b7280)', whiteSpace: 'nowrap', verticalAlign: 'bottom' };
const td = { textAlign: 'right', padding: '6px 8px', fontSize: 13, whiteSpace: 'nowrap' };
const left = { textAlign: 'left' };
const box = { border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', overflowX: 'auto' };
const btn = { background: 'var(--navy, #00395C)', color: '#fff', border: 0, borderRadius: 6, padding: '7px 12px', fontWeight: 600, cursor: 'pointer' };
const btn2 = { background: '#fff', color: 'var(--navy, #00395C)', border: '1px solid #c5d0da', borderRadius: 6, padding: '6px 11px', fontWeight: 600, cursor: 'pointer' };
const input = { border: '1px solid #c5d0da', borderRadius: 5, padding: '5px 7px', fontSize: 13 };
const lbl = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, color: 'var(--gray-600, #4b5563)' };

function Badge({ code }) {
  const s = STATUS[code];
  if (!s) return null;
  return <span title={s[0]} style={{ background: s[1], color: s[2], borderRadius: 4, padding: '1px 6px', fontWeight: 700, fontSize: 12 }}>{code}</span>;
}

const tabBtn = (on) => ({ background: 'none', border: 0, borderBottom: on ? '3px solid var(--navy, #00395C)' : '3px solid transparent', padding: '8px 12px', fontWeight: on ? 700 : 500, color: on ? 'var(--navy, #00395C)' : '#4b5563', cursor: 'pointer', fontSize: 14 });

export default function Attendance() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('grid');
  const [err, setErr] = useState('');
  const [pipsOk, setPipsOk] = useState(false);
  const loadMe = useCallback(() => attendance('me').then(setMe, (e) => setErr(e.message || 'Could not open Attendance.')), []);
  useEffect(() => {
    loadMe();
    attendance('pips').then(() => setPipsOk(true), () => setPipsOk(false));
  }, [loadMe]);

  if (err) return <div style={{ padding: 24 }}><h1 style={{ fontSize: 22 }}>Attendance</h1><p style={{ color: '#b42318' }}>{err}</p></div>;
  if (!me) return <div style={{ padding: 24 }}><h1 style={{ fontSize: 22 }}>Attendance</h1><p>Loading…</p></div>;
  const isAdmin = me.access.admin;
  const tabs = [
    ['grid', 'Grid'],
    ['schedule', 'Schedule'],
    ['punches', 'Missed punches'],
    ...(isAdmin || me.access.manage.length || me.canApprove ? [['points', 'Points']] : []),
    ...(isAdmin ? [['holidays', 'Holidays'], ['summary', 'Month summary']] : []),
    ...(pipsOk && !isAdmin ? [['pips', 'PIPs']] : []),
    ['alerts', 'My alerts'],
    ...(isAdmin ? [['team', 'Team']] : []),
  ];
  return (
    <div style={{ padding: 24, maxWidth: 1500 }}>
      <h1 style={{ fontSize: 22, margin: '0 0 4px' }}>Attendance</h1>
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #e5e7eb', marginBottom: 16, flexWrap: 'wrap' }}>
        {tabs.map(([k, label]) => <button key={k} type="button" style={tabBtn(tab === k)} onClick={() => setTab(k)}>{label}</button>)}
      </div>
      {tab === 'grid' && <TeamGrid me={me} />}
      {tab === 'schedule' && <Schedule me={me} />}
      {tab === 'punches' && <Punches me={me} />}
      {tab === 'points' && <Points me={me} />}
      {tab === 'holidays' && isAdmin && <Holidays />}
      {tab === 'summary' && isAdmin && <MonthSummary />}
      {tab === 'pips' && <Pips />}
      {tab === 'alerts' && <Alerts me={me} onSaved={loadMe} />}
      {tab === 'team' && isAdmin && <TeamRoster me={me} />}
    </div>
  );
}

function MonthSummary() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [openKey, setOpenKey] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (m) => {
    setLoading(true); setErr('');
    try { setData(await attendance('month', { month: m })); } catch (e) { setErr(e.message || 'Could not load attendance.'); setData(null); }
    setLoading(false);
  }, []);
  useEffect(() => { load(month); setReport(null); }, [month, load]);

  const person = useMemo(() => (data && data.people.find((p) => p.key === openKey)) || null, [data, openKey]);

  const previewReport = async () => {
    setBusy(true); setErr(''); setNote('');
    try { setReport(await attendance('report-preview', { month })); } catch (e) { setErr(e.message || 'Could not build the report.'); }
    setBusy(false);
  };
  const draftReport = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Put the ${monthLabel(month)} report in Mercedes's Outlook drafts? It will not be sent.`)) return;
    setBusy(true); setErr(''); setNote('');
    try {
      await attendance('report-draft', { month });
      setNote(`The ${monthLabel(month)} report is in Mercedes's Outlook drafts. Review it there and send it when ready.`);
      setReport(null);
    } catch (e) { setErr(e.message || 'Could not make the draft.'); }
    setBusy(false);
  };

  return (
    <div>
      <p style={{ margin: '0 0 14px', color: 'var(--gray-600, #4b5563)', maxWidth: 860 }}>
        Time off, overtime and late or early days from TimeClock Plus, plus what you enter here. Overtime is hours
        worked over 40 in a Monday to Sunday week, Crab and Cruise hours first, and a week counts in the month its
        Sunday falls in. Only Greg and Mercedes can open this page.
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <label style={{ ...lbl, flexDirection: 'row', alignItems: 'center', gap: 8, fontSize: 13 }}>
          Month <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} style={input} />
        </label>
        <button type="button" style={btn2} disabled={loading} onClick={() => load(month)}>{loading ? 'Loading…' : 'Refresh'}</button>
        <button type="button" style={btn} disabled={busy || loading || !data} onClick={previewReport}>Monthly report</button>
      </div>

      {note && <div style={{ background: '#e8f5ec', color: '#1c7c43', padding: '9px 12px', borderRadius: 6, marginBottom: 12 }}>{note}</div>}
      {err && <div style={{ background: '#fdeaea', color: '#b42318', padding: '9px 12px', borderRadius: 6, marginBottom: 12 }}>{err}</div>}

      {report && (
        <div style={{ ...box, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            <div><b>Goes to Mercedes's drafts</b> ({report.mailbox}). It is never sent automatically. No PIPs, points or notes; missed punches carry their reason.</div>
            <div>To: {report.to.join(', ')}</div>
            <div>Cc: {report.cc.join(', ')}</div>
            <div>Subject: {report.subject}</div>
            {report.missing && report.missing.length > 0 && <div style={{ color: '#b42318' }}>Some recipients have no email in CPx Users.</div>}
          </div>
          {/* The HTML is built on our server from our own numbers. */}
          <div style={{ border: '1px solid #eef0f2', padding: 10, maxHeight: 420, overflow: 'auto' }} dangerouslySetInnerHTML={{ __html: report.html }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button type="button" style={btn} disabled={busy} onClick={draftReport}>Put it in Mercedes's drafts</button>
            <button type="button" style={btn2} onClick={() => setReport(null)}>Close</button>
          </div>
        </div>
      )}

      {loading && !data && <p>Reading the time clock…</p>}
      {data && <MonthGrid data={data} openKey={openKey} onOpen={(k) => setOpenKey(k === openKey ? null : k)} />}
      {data && person && <PersonDetail key={person.key + month} p={data && person} data={data} onSaved={() => load(month)} onClose={() => setOpenKey(null)} />}
      {data && <AddPerson data={data} onSaved={() => load(month)} />}
      {data && data.canSeePips && <Pips />}
    </div>
  );
}

function MonthGrid({ data, openKey, onOpen }) {
  const people = data.people;
  if (!people.length) return <p>No one has time in TimeClock Plus for {monthLabel(data.month)}.</p>;
  const tot = (f) => people.reduce((a, p) => a + (Number(f(p.summary)) || 0), 0);
  return (
    <div style={{ ...box, marginBottom: 18 }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontVariantNumeric: 'tabular-nums' }}>
        <thead>
          <tr>
            <th style={{ ...th, ...left }} rowSpan={2}>Person</th>
            <th style={th} rowSpan={2}>Days present</th>
            <th style={{ ...th, textAlign: 'center', borderBottom: '1px solid #eef0f2' }} colSpan={5}>Paid time off (hours)</th>
            <th style={{ ...th, textAlign: 'center', borderBottom: '1px solid #eef0f2' }} colSpan={4}>Unpaid and out (days)</th>
            <th style={{ ...th, textAlign: 'center', borderBottom: '1px solid #eef0f2' }} colSpan={2}>Overtime (hours)</th>
            <th style={th} rowSpan={2}>Late</th>
            <th style={th} rowSpan={2}>Left early</th>
            <th style={th} rowSpan={2}>Missed punches</th>
            <th style={th} rowSpan={2} title="Points in the 12 months to the end of this month">Points</th>
            <th style={th} rowSpan={2} title="Flex days used this year / allowed">Flex days</th>
            <th style={th} rowSpan={2}>Notes</th>
          </tr>
          <tr>
            <th style={th}>PTO</th><th style={th}>Sick</th><th style={th}>Holiday</th><th style={th}>Flex</th><th style={th}>Other</th>
            <th style={th}>Unpaid</th><th style={th}>Unpaid hrs</th><th style={th}>Called out</th><th style={th} title="Weekdays with no hours and no leave. Mark them called out or unpaid in the person's detail.">No time</th>
            <th style={th}>Crab Place</th><th style={th}>C&amp;C</th>
          </tr>
        </thead>
        <tbody>
          {people.map((p) => {
            const s = p.summary;
            return (
              <tr key={p.key} onClick={() => onOpen(p.key)} style={{ borderTop: '1px solid #eef0f2', cursor: 'pointer', background: openKey === p.key ? '#eef4fa' : undefined }}>
                <td style={{ ...td, ...left, fontWeight: 600, color: 'var(--navy, #00395C)' }}>
                  {p.name}{p.salaried ? <span style={{ fontWeight: 400, color: '#6b7280' }}> (salaried)</span> : null}
                </td>
                <td style={td}>{cnt(s.present)}</td>
                <td style={td}>{hrs(s.paidHours.pto)}</td>
                <td style={td}>{hrs(s.paidHours.sick)}</td>
                <td style={td}>{hrs(s.paidHours.holiday)}</td>
                <td style={td}>{hrs(s.paidHours.flex)}</td>
                <td style={td}>{hrs(s.paidHours.other)}</td>
                <td style={td}>{cnt(s.unpaidDays)}</td>
                <td style={td}>{hrs(s.unpaidHours)}</td>
                <td style={td}>{cnt(s.calledOut)}</td>
                <td style={{ ...td, color: s.noTime ? '#8a5a00' : undefined, fontWeight: s.noTime ? 700 : undefined }}>{cnt(s.noTime)}</td>
                <td style={td}>{hrs(s.otCP)}</td>
                <td style={td}>{hrs(s.otCC)}</td>
                <td style={td}>{p.settings.start_time ? cnt(s.late) : <span style={{ color: '#9ca3af' }} title="Set a standard start time">set</span>}</td>
                <td style={td}>{p.settings.end_time ? cnt(s.early) : <span style={{ color: '#9ca3af' }} title="Set a standard end time">set</span>}</td>
                <td style={td} title={s.possibleMissed ? `${s.possibleMissed} possible missed punch(es) to review` : ''}>
                  {cnt(s.missedPunches)}{s.possibleMissed ? <span style={{ color: '#8a5a00' }}> +{s.possibleMissed}?</span> : null}
                </td>
                <td style={td}>{cnt(s.points12)}</td>
                <td style={td}>{s.flexAllowed == null ? cnt(s.flexUsed) : `${s.flexUsed} of ${s.flexAllowed}`}</td>
                <td style={td}>{cnt(s.notes)}</td>
              </tr>
            );
          })}
          <tr style={{ borderTop: '2px solid var(--navy, #00395C)', fontWeight: 700 }}>
            <td style={{ ...td, ...left }}>Total</td>
            <td style={td}>{cnt(tot((s) => s.present))}</td>
            {['pto', 'sick', 'holiday', 'flex', 'other'].map((k) => <td key={k} style={td}>{hrs(tot((s) => s.paidHours[k]))}</td>)}
            <td style={td}>{cnt(tot((s) => s.unpaidDays))}</td>
            <td style={td}>{hrs(tot((s) => s.unpaidHours))}</td>
            <td style={td}>{cnt(tot((s) => s.calledOut))}</td>
            <td style={td}>{cnt(tot((s) => s.noTime))}</td>
            <td style={td}>{hrs(tot((s) => s.otCP))}</td>
            <td style={td}>{hrs(tot((s) => s.otCC))}</td>
            <td style={td} colSpan={6} />
          </tr>
        </tbody>
      </table>
      <div style={{ padding: '8px 10px', fontSize: 12, color: '#6b7280', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        Click a person for the day by day detail. A number with a question mark under Missed punches is a TimeClock Plus
        entry typed in well after its clock-in time: possibly a missed punch, worth a look.
      </div>
    </div>
  );
}

function PersonDetail({ p, data, onSaved, onClose }) {
  const [s, setS] = useState({ ...p.settings });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [entry, setEntry] = useState({ entry_date: '', kind: p.salaried ? 'off_pto' : 'note', hours: '', points: '', note: '' });
  const kinds = data.entryKinds || {};

  const save = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      await attendance('save-settings', { settings: { person_key: p.key, person_name: p.name, ...s } });
      setMsg('Saved.'); onSaved();
    } catch (e) { setErr(e.message || 'Could not save.'); }
    setBusy(false);
  };
  const add = async (dateOverride) => {
    const body = { ...entry, entry_date: dateOverride || entry.entry_date, person_key: p.key, person_name: p.name };
    setBusy(true); setErr(''); setMsg('');
    try {
      await attendance('add-entry', { entry: body });
      setEntry({ ...entry, hours: '', points: '', note: '' });
      setMsg('Added.'); onSaved();
    } catch (e) { setErr(e.message || 'Could not add that.'); }
    setBusy(false);
  };
  const del = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove this entry?')) return;
    setBusy(true); setErr('');
    try { await attendance('delete-entry', { id }); onSaved(); } catch (e) { setErr(e.message || 'Could not remove it.'); }
    setBusy(false);
  };

  return (
    <div style={{ ...box, padding: 16, marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <h2 style={{ fontSize: 18, margin: '0 0 10px' }}>{p.name}, {monthLabel(data.month)}</h2>
        <button type="button" style={btn2} onClick={onClose}>Close</button>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        <label style={lbl}>Standard start
          <input type="time" value={s.start_time || ''} onChange={(e) => setS({ ...s, start_time: e.target.value })} style={input} />
          {s.suggested_start && !s.start_time && <span style={{ fontSize: 11 }}>Usually about {clock(s.suggested_start)}</span>}
        </label>
        <label style={lbl}>Standard end
          <input type="time" value={s.end_time || ''} onChange={(e) => setS({ ...s, end_time: e.target.value })} style={input} />
          {s.suggested_end && !s.end_time && <span style={{ fontSize: 11 }}>Usually about {clock(s.suggested_end)}</span>}
        </label>
        <label style={lbl}>Grace (minutes)
          <input type="number" min="0" max="120" value={s.grace_min} onChange={(e) => setS({ ...s, grace_min: e.target.value })} style={{ ...input, width: 80 }} />
        </label>
        <label style={lbl}>Flex days allowed this year
          <input type="number" min="0" step="0.5" value={s.flex_allowed == null ? '' : s.flex_allowed} onChange={(e) => setS({ ...s, flex_allowed: e.target.value })} style={{ ...input, width: 90 }} />
        </label>
        <label style={{ ...lbl, flexDirection: 'row', alignItems: 'center' }}>
          <input type="checkbox" checked={!!s.salaried} onChange={(e) => setS({ ...s, salaried: e.target.checked })} /> Salaried, not on the clock
        </label>
        <label style={{ ...lbl, flexDirection: 'row', alignItems: 'center' }} title="Seasonal crew whose days off vary: a weekday with no time is a day off, not an absence">
          <input type="checkbox" checked={!!s.no_schedule} onChange={(e) => setS({ ...s, no_schedule: e.target.checked })} /> No set schedule
        </label>
        <label style={{ ...lbl, flexDirection: 'row', alignItems: 'center' }}>
          <input type="checkbox" checked={!!s.hidden} onChange={(e) => setS({ ...s, hidden: e.target.checked })} /> Hide from the page
        </label>
        <button type="button" style={btn} disabled={busy} onClick={save}>Save settings</button>
      </div>

      {err && <div style={{ background: '#fdeaea', color: '#b42318', padding: '7px 10px', borderRadius: 6, marginBottom: 10 }}>{err}</div>}
      {msg && <div style={{ background: '#e8f5ec', color: '#1c7c43', padding: '7px 10px', borderRadius: 6, marginBottom: 10 }}>{msg}</div>}

      <h3 style={{ fontSize: 14, margin: '6px 0' }}>Add an entry</h3>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <label style={lbl}>Date<input type="date" value={entry.entry_date} onChange={(e) => setEntry({ ...entry, entry_date: e.target.value })} style={input} /></label>
        <label style={lbl}>Kind
          <select value={entry.kind} onChange={(e) => setEntry({ ...entry, kind: e.target.value })} style={input}>
            {Object.entries(kinds).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        {/^off_/.test(entry.kind) && <label style={lbl}>Hours (blank = 8)<input type="number" min="0" max="24" step="0.25" value={entry.hours} onChange={(e) => setEntry({ ...entry, hours: e.target.value })} style={{ ...input, width: 80 }} /></label>}
        {entry.kind === 'point' && <label style={lbl}>Points<input type="number" min="0.5" max="10" step="0.5" value={entry.points} onChange={(e) => setEntry({ ...entry, points: e.target.value })} style={{ ...input, width: 80 }} /></label>}
        <label style={{ ...lbl, flex: '1 1 240px' }}>Note<input type="text" value={entry.note} onChange={(e) => setEntry({ ...entry, note: e.target.value })} style={input} /></label>
        <button type="button" style={btn} disabled={busy || !entry.entry_date} onClick={() => add()}>Add</button>
      </div>

      <h3 style={{ fontSize: 14, margin: '6px 0' }}>Overtime by week</h3>
      <div style={{ ...box, marginBottom: 16 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontVariantNumeric: 'tabular-nums' }}>
          <thead><tr>
            <th style={{ ...th, ...left }}>Week</th><th style={th}>Worked</th><th style={th}>C&amp;C hours</th><th style={th}>Overtime</th><th style={th}>OT Crab Place</th><th style={th}>OT C&amp;C</th>
          </tr></thead>
          <tbody>
            {p.weeks.map((w) => (
              <tr key={w.start} style={{ borderTop: '1px solid #eef0f2' }}>
                <td style={{ ...td, ...left }}>{fmtDay(w.start)} to {fmtDay(w.end)}{w.open ? ' (not over yet)' : ''}</td>
                <td style={td}>{hrs(w.worked)}</td><td style={td}>{hrs(w.cc)}</td><td style={td}>{hrs(w.ot)}</td>
                <td style={td}>{hrs(w.otCP)}</td><td style={td}>{hrs(w.otCC)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ fontSize: 14, margin: '6px 0' }}>Day by day</h3>
      <div style={box}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontVariantNumeric: 'tabular-nums' }}>
          <thead><tr>
            <th style={{ ...th, ...left }}>Date</th><th style={{ ...th, ...left }}>Status</th><th style={th}>Worked</th>
            <th style={th}>First punch</th><th style={th}>Last punch</th><th style={{ ...th, ...left }}>Time off</th>
            <th style={{ ...th, ...left }}>Late / early</th><th style={{ ...th, ...left }}>Entries and flags</th>
          </tr></thead>
          <tbody>
            {p.days.map((d) => (
              <tr key={d.date} style={{ borderTop: '1px solid #eef0f2', background: d.weekend ? '#fafafa' : undefined, color: d.weekend && !d.worked ? '#9ca3af' : undefined }}>
                <td style={{ ...td, ...left }}>{fmtDow(d.date)} {fmtDay(d.date)}</td>
                <td style={{ ...td, ...left }}>
                  <Badge code={d.status} /> {d.status && STATUS[d.status] ? <span style={{ fontSize: 12 }}>{STATUS[d.status][0]}</span> : null}
                  {d.status === 'N' && (
                    <span style={{ marginLeft: 6 }}>
                      <button type="button" style={{ ...btn2, padding: '1px 6px', fontSize: 11 }} disabled={busy} onClick={() => attendance('add-entry', { entry: { person_key: p.key, person_name: p.name, entry_date: d.date, kind: 'called_out' } }).then(onSaved, (e) => setErr(e.message))}>Called out</button>{' '}
                      <button type="button" style={{ ...btn2, padding: '1px 6px', fontSize: 11 }} disabled={busy} onClick={() => attendance('add-entry', { entry: { person_key: p.key, person_name: p.name, entry_date: d.date, kind: 'unpaid' } }).then(onSaved, (e) => setErr(e.message))}>Unpaid</button>
                    </span>
                  )}
                </td>
                <td style={td}>{hrs(d.worked)}</td>
                <td style={td}>{clock(d.first)}</td>
                <td style={td}>{d.open ? 'still in' : clock(d.last)}</td>
                <td style={{ ...td, ...left }}>{d.leave.map((l) => `${l.label} ${l.hours.toFixed(2)}`).join(', ')}</td>
                <td style={{ ...td, ...left, color: '#b42318' }}>
                  {d.lateBy != null ? `Late ${d.lateBy} min` : ''}{d.lateBy != null && d.earlyBy != null ? ', ' : ''}{d.earlyBy != null ? `Left ${d.earlyBy} min early` : ''}
                </td>
                <td style={{ ...td, ...left, whiteSpace: 'normal', minWidth: 260 }}>
                  {d.flags.map((f, i) => <div key={i} style={{ color: '#8a5a00' }}>{f}</div>)}
                  {d.entries.map((e) => (
                    <div key={e.id}>
                      <b>{e.label}</b>{e.points ? ` (${e.points})` : ''}{e.hours != null ? ` ${Number(e.hours).toFixed(2)} hrs` : ''}{e.note ? `: ${e.note}` : ''}
                      {e.by ? <span style={{ color: '#9ca3af' }}> ({e.by})</span> : null}{' '}
                      <button type="button" onClick={() => del(e.id)} disabled={busy} style={{ background: 'none', border: 0, color: '#6b7280', cursor: 'pointer', fontSize: 11, textDecoration: 'underline' }}>remove</button>
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Someone with no time in TCP this month (a salaried person, or a new hire) gets a row
// once they have a setting marked salaried or an entry this month.
function AddPerson({ data, onSaved }) {
  const shown = new Set(data.people.map((p) => p.key));
  const options = (data.roster || []).filter((r) => !shown.has(r.key));
  const [key, setKey] = useState('');
  const [err, setErr] = useState('');
  if (!options.length) return null;
  const add = async () => {
    const r = options.find((o) => o.key === key);
    if (!r) return;
    setErr('');
    try { await attendance('save-settings', { settings: { person_key: r.key, person_name: r.name, salaried: true } }); onSaved(); } catch (e) { setErr(e.message); }
  };
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 22, fontSize: 13, flexWrap: 'wrap' }}>
      <span>Show a salaried person who is not on the clock:</span>
      <select value={key} onChange={(e) => setKey(e.target.value)} style={input}>
        <option value="">Pick a person</option>
        {options.map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}
      </select>
      <button type="button" style={btn2} disabled={!key} onClick={add}>Add as salaried</button>
      {err && <span style={{ color: '#b42318' }}>{err}</span>}
    </div>
  );
}

const blankPip = { person_name: '', reason: '', step: '', notes: '', issue_date: '', follow_30: '', follow_60: '', follow_90: '', closed: false };

function Pips() {
  const [pips, setPips] = useState(null);
  const [edit, setEdit] = useState(null);
  const [err, setErr] = useState('');
  const [showClosed, setShowClosed] = useState(false);
  useEffect(() => { attendance('pips').then((d) => setPips(d.pips), (e) => setErr(e.message)); }, []);

  const save = async () => {
    setErr('');
    try { const d = await attendance('pip-save', { pip: edit }); setPips(d.pips); setEdit(null); } catch (e) { setErr(e.message); }
  };
  const del = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this PIP for good? Marking it closed keeps the record.')) return;
    try { const d = await attendance('pip-delete', { id }); setPips(d.pips); setEdit(null); } catch (e) { setErr(e.message); }
  };
  const list = (pips || []).filter((x) => showClosed || !x.closed);

  return (
    <div style={{ marginTop: 10 }}>
      <h2 style={{ fontSize: 18, margin: '0 0 4px' }}>Performance improvement plans</h2>
      <p style={{ margin: '0 0 10px', color: 'var(--gray-600, #4b5563)', fontSize: 13 }}>
        Only Mercedes, Carman, Rachael, Matt Jr, Matt, Brenda and Montel can see PIPs. They never go in the monthly report.
      </p>
      {err && <div style={{ background: '#fdeaea', color: '#b42318', padding: '7px 10px', borderRadius: 6, marginBottom: 10 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
        <button type="button" style={btn} onClick={() => setEdit({ ...blankPip })}>Add a PIP</button>
        <label style={{ fontSize: 13 }}><input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> Show closed</label>
      </div>
      {edit && (
        <div style={{ ...box, padding: 12, marginBottom: 12, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={lbl}>Employee<input value={edit.person_name} onChange={(e) => setEdit({ ...edit, person_name: e.target.value })} style={input} /></label>
          <label style={lbl}>Reason<input value={edit.reason} onChange={(e) => setEdit({ ...edit, reason: e.target.value })} style={{ ...input, width: 220 }} /></label>
          <label style={lbl}>Step<input value={edit.step || ''} placeholder="Step 1: Formal Verbal" onChange={(e) => setEdit({ ...edit, step: e.target.value })} style={input} /></label>
          <label style={lbl}>Issue date<input type="date" value={edit.issue_date || ''} onChange={(e) => setEdit({ ...edit, issue_date: e.target.value })} style={input} /></label>
          <label style={lbl}>30 day follow-up<input type="date" value={edit.follow_30 || ''} onChange={(e) => setEdit({ ...edit, follow_30: e.target.value })} style={input} /></label>
          <label style={lbl}>60 day<input type="date" value={edit.follow_60 || ''} onChange={(e) => setEdit({ ...edit, follow_60: e.target.value })} style={input} /></label>
          <label style={lbl}>90 day<input type="date" value={edit.follow_90 || ''} onChange={(e) => setEdit({ ...edit, follow_90: e.target.value })} style={input} /></label>
          <label style={{ ...lbl, flex: '1 1 100%' }}>Notes<textarea rows={3} value={edit.notes || ''} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} style={input} /></label>
          <label style={{ ...lbl, flexDirection: 'row', alignItems: 'center' }}><input type="checkbox" checked={!!edit.closed} onChange={(e) => setEdit({ ...edit, closed: e.target.checked })} /> Closed</label>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Blank follow-ups fill in 30, 60 and 90 days after the issue date.</span>
          <button type="button" style={btn} onClick={save}>Save</button>
          <button type="button" style={btn2} onClick={() => setEdit(null)}>Cancel</button>
          {edit.id && <button type="button" style={{ ...btn2, color: '#b42318' }} onClick={() => del(edit.id)}>Delete</button>}
        </div>
      )}
      {pips && !list.length && <p style={{ fontSize: 13 }}>No open PIPs.</p>}
      {list.length > 0 && (
        <div style={box}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>
              <th style={{ ...th, ...left }}>Employee</th><th style={{ ...th, ...left }}>Reason</th><th style={{ ...th, ...left }}>Step</th>
              <th style={{ ...th, ...left }}>Issued</th><th style={{ ...th, ...left }}>30 day</th><th style={{ ...th, ...left }}>60 day</th><th style={{ ...th, ...left }}>90 day</th>
              <th style={{ ...th, ...left }}>Notes</th>
            </tr></thead>
            <tbody>
              {list.map((x) => (
                <tr key={x.id} onClick={() => setEdit({ ...x })} style={{ borderTop: '1px solid #eef0f2', cursor: 'pointer', color: x.closed ? '#9ca3af' : undefined }}>
                  <td style={{ ...td, ...left, fontWeight: 600 }}>{x.person_name}{x.closed ? ' (closed)' : ''}</td>
                  <td style={{ ...td, ...left, whiteSpace: 'normal' }}>{x.reason}</td>
                  <td style={{ ...td, ...left }}>{x.step}</td>
                  <td style={{ ...td, ...left }}>{fmtDay(x.issue_date)}</td>
                  <td style={{ ...td, ...left }}>{fmtDay(x.follow_30)}</td>
                  <td style={{ ...td, ...left }}>{fmtDay(x.follow_60)}</td>
                  <td style={{ ...td, ...left }}>{fmtDay(x.follow_90)}</td>
                  <td style={{ ...td, ...left, whiteSpace: 'pre-wrap', minWidth: 240 }}>{x.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
